import { fetchAuthSession, signIn, signOut } from 'aws-amplify/auth'
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js'
import React, { ReactElement, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectIsVerifyingUserFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectVerificationEmailResentFromState,
  selectVerifyEmailTopLevelErrorFromState,
} from 'src/app/Signup/redux/signup.selectors'
import { UserStatus } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import {
  useSendPhoneCode,
  useVerifyPhoneCode,
} from 'src/queries/phoneVerification/domain/phoneVerification.queries'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  CONFIRM_COGNITO_SIGNIN_SUCCESS,
  getCognitoSigninThenPush,
  POST_USER_VERIFICATION_SUCCESS,
  postAuthType,
  postUserResendVerification,
  postUserVerification,
  postUserWithoutPhoneVerificationAndSignIn,
} from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectEmailFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectUnauthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import {
  selectBrandId,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { configureAmplify } from 'src/utils/cognito.utils'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'

function extractErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { msg?: string } } })
      .response
    return response?.data?.msg ?? undefined
  }
  return undefined
}

import EditPhoneModal from '../EditPhoneModal/EditPhoneModal'
import VerifyEmailModal from '../VerifyEmailModal/VerifyEmailModal'
import VerifyPhoneModal from '../VerifyPhoneModal/VerifyPhoneModal'

type FlowStep = 'verifyEmail' | 'authenticating' | 'verifyPhone' | 'editPhone'

type PhoneRequiredVerificationFlowProps = {
  password: string
  phoneIsoCountry: string
  phone: string
}

export default function PhoneRequiredVerificationFlow(
  props: PhoneRequiredVerificationFlowProps,
): ReactElement {
  const {
    password,
    phoneIsoCountry: initialPhoneIsoCountry,
    phone: initialPhone,
  } = props

  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  const email = useSelector(selectEmailFromState)
  const authType = useSelector(selectAuthTypeFromState)
  const queryParams = useSelector(selectQueryParams)
  const env = useSelector(selectEnvironmentFromState)
  const featureFlagger = useSelector(selectFeatureFlaggerFromState)

  const { orgName, inviteeEmail, inviterName, inviterEmail } = queryParams
  const signupAndJoin = !!(
    orgName &&
    inviteeEmail &&
    inviterName &&
    inviterEmail
  )

  // Email verification selectors
  const verifyEmailLoading = useSelector(selectIsVerifyingUserFromState)
  const verifyEmailError = useSelector(selectVerifyEmailTopLevelErrorFromState)
  const verificationEmailResent = useSelector(
    selectVerificationEmailResentFromState,
  )
  const resendError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )

  // Phone verification mutations (new authenticated endpoints)
  const sendPhoneCode = useSendPhoneCode()
  const verifyPhoneCode = useVerifyPhoneCode()

  // Flow state
  const [step, setStep] = useState<FlowStep>('verifyEmail')
  const [phoneIsoCountry, setPhoneIsoCountry] = useState(initialPhoneIsoCountry)
  const [phone, setPhone] = useState(initialPhone)
  const [phoneVerifyError, setPhoneVerifyError] = useState<string | undefined>()
  const [phoneSendError, setPhoneSendError] = useState<string | undefined>()
  const [phoneSendSuccess, setPhoneSendSuccess] = useState(false)

  const fullPhoneNumber =
    phoneIsoCountry && phone
      ? `+${getCountryCallingCode(phoneIsoCountry as CountryCode)}${phone}`
      : ''

  const ensureAuthType = useCallback(() => {
    if (!authType) {
      dispatch(postAuthType({ data: email }))
    }
  }, [authType, dispatch, email])

  /**
   * After email verification succeeds, silently authenticate with Cognito
   * to get a JWT (no navigation or redirects), then send the phone
   * verification code using the new authenticated endpoint.
   */
  const authenticateAndSendPhoneCode = useCallback(async () => {
    setStep('authenticating')

    try {
      // Configure Amplify with the correct user pool before signing in
      const axiosConfig = selectUnauthedJsonApiRequestConfig(
        dispatch((_, getState) => getState()),
      )
      const ssoEnabled = isSSOLoginEnabled(featureFlagger)

      try {
        await configureAmplify(env, { email, axiosConfig, ssoEnabled })
      } catch {
        // If authconfig fails, continue with existing config
      }

      // Sign out any existing session to avoid conflicts
      try {
        const session = await fetchAuthSession()
        if (session.tokens !== undefined) {
          await signOut()
        }
      } catch {
        // No existing session, continue
      }

      // Silent Cognito sign-in — just get the JWT, no navigation
      const brandId = selectBrandId(dispatch((_, getState) => getState()))
      const locale = selectNullSafeLocaleFromState(
        dispatch((_, getState) => getState()),
      )

      const response = await signIn({
        username: email,
        password: password,
        options: { clientMetadata: { brandId, locale } },
      })

      if (response.isSignedIn) {
        // Store JWT in Redux so authenticated API calls work
        const { tokens } = await fetchAuthSession()
        dispatch({
          type: CONFIRM_COGNITO_SIGNIN_SUCCESS,
          payload: { jwt: tokens?.accessToken.toString() },
        })
      }

      // Now we're authenticated — send phone verification code
      await sendPhoneCode.mutateAsync({ phone: fullPhoneNumber })

      setStep('verifyPhone')
    } catch (err) {
      setPhoneSendError('Failed to send verification code. Please try again.')
      setStep('verifyEmail')
    }
  }, [
    dispatch,
    email,
    password,
    env,
    featureFlagger,
    fullPhoneNumber,
    sendPhoneCode,
  ])

  // --- Email verification handlers ---

  const handleEmailVerify = useCallback(
    (code: string): void => {
      ensureAuthType()
      dispatch(postUserVerification(code, email)).then((res): void => {
        if (
          res.type === POST_USER_VERIFICATION_SUCCESS &&
          res.payload.msg === UserStatus.EmailVerified
        ) {
          // Email verified — now authenticate and send phone code
          authenticateAndSendPhoneCode()
        } else if (
          res.type === POST_USER_VERIFICATION_SUCCESS &&
          res.payload.msg === UserStatus.Verified
        ) {
          // User is already fully verified (no phone needed)
          dispatch(
            postUserWithoutPhoneVerificationAndSignIn(
              email,
              password,
              signupAndJoin,
            ),
          )
        }
      })
    },
    [
      dispatch,
      email,
      password,
      signupAndJoin,
      ensureAuthType,
      authenticateAndSendPhoneCode,
    ],
  )

  const handleEmailResend = useCallback(
    (emailAddr: string): void => {
      dispatch(postUserResendVerification(emailAddr))
    },
    [dispatch],
  )

  const handleEmailGoBack = useCallback((): void => {
    dispatch(createSetIsExtraVerificationPopupOpen(false))
  }, [dispatch])

  // --- Phone verification handlers ---

  const handlePhoneVerify = useCallback(
    async (code: string): Promise<void> => {
      analytics?.track('verify phone - sign up')
      setPhoneVerifyError(undefined)

      try {
        await verifyPhoneCode.mutateAsync({ code })

        // Phone verified — close modal, load user data, and redirect
        dispatch(createSetIsExtraVerificationPopupOpen(false))
        await dispatch(getCognitoSigninThenPush(false, signupAndJoin))
      } catch (err: unknown) {
        setPhoneVerifyError(
          extractErrorMessage(err) ||
            'Invalid verification code. Please try again.',
        )
      }
    },
    [dispatch, signupAndJoin, analytics, verifyPhoneCode],
  )

  const handlePhoneResend = useCallback(async (): Promise<void> => {
    analytics?.track('phone verification - send new code')
    setPhoneSendError(undefined)
    setPhoneSendSuccess(false)

    try {
      await sendPhoneCode.mutateAsync({ phone: fullPhoneNumber })
      setPhoneSendSuccess(true)
    } catch (err: unknown) {
      setPhoneSendError(
        extractErrorMessage(err) ||
          'Unable to send a new code. Please try again.',
      )
    }
  }, [analytics, sendPhoneCode, fullPhoneNumber])

  const handleEditPhone = useCallback((): void => {
    setPhoneSendError(undefined)
    setStep('editPhone')
  }, [])

  // --- Edit phone handlers ---

  const handleEditPhoneSubmit = useCallback(
    async (newIsoCountry: string, newPhone: string): Promise<void> => {
      const newFullPhone = `+${getCountryCallingCode(newIsoCountry as CountryCode)}${newPhone}`

      try {
        // Call sendcode with the new phone number — this persists it and sends a new code
        await sendPhoneCode.mutateAsync({ phone: newFullPhone })

        setPhoneIsoCountry(newIsoCountry)
        setPhone(newPhone)
        setPhoneVerifyError(undefined)
        setStep('verifyPhone')
      } catch (err: unknown) {
        setPhoneSendError(
          extractErrorMessage(err) ||
            'Failed to update phone number. Please try again.',
        )
      }
    },
    [sendPhoneCode],
  )

  const handleEditPhoneGoBack = useCallback((): void => {
    setPhoneSendError(undefined)
    setStep('verifyPhone')
  }, [])

  const isAuthenticating = step === 'authenticating'

  return (
    <>
      <VerifyEmailModal
        isOpen={step === 'verifyEmail' || isAuthenticating}
        email={email}
        isVerifying={verifyEmailLoading || isAuthenticating}
        verifyError={verifyEmailError}
        resendError={resendError}
        verificationResent={verificationEmailResent}
        onVerify={handleEmailVerify}
        onResend={handleEmailResend}
        onGoBack={handleEmailGoBack}
        hideGoBack={signupAndJoin}
      />

      <VerifyPhoneModal
        isOpen={step === 'verifyPhone'}
        phoneNumber={fullPhoneNumber}
        isVerifying={verifyPhoneCode.isPending}
        verifyError={phoneVerifyError}
        resendError={phoneSendError}
        verificationResent={phoneSendSuccess}
        onVerify={handlePhoneVerify}
        onResend={handlePhoneResend}
        onEditPhone={handleEditPhone}
      />

      <EditPhoneModal
        isOpen={step === 'editPhone'}
        initialPhoneIsoCountry={phoneIsoCountry}
        initialPhone={phone}
        isSubmitting={sendPhoneCode.isPending}
        phoneError={phoneSendError}
        onSubmit={handleEditPhoneSubmit}
        onGoBack={handleEditPhoneGoBack}
        onClearError={() => setPhoneSendError(undefined)}
      />
    </>
  )
}
